import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Crown, Star, Compass, Shield, Clock, Droplets, Package, Users, Sparkles, Lock, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import LazyImage from '../components/LazyImage';
import { RevealOnScroll, LazyMount } from '../components/ScrollReveal';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import TrustStrip from '../components/TrustStrip';
import SEOHead from '../components/SEOHead';
import OrganizationSchema from '../components/OrganizationSchema';

const API = `${window.location.origin}/api`;
const DEFAULT_HERO_IMG = "/api/files/jsfragrance/products/c692ac28-39b5-4b42-8b79-1b55787aa0be.png";
const LOGO_URL = "/api/files/jsfragrance/products/be3c5fd6-5c5e-4e28-9eca-fed1e5e06eea.png";

export default function HomePage() {
  const { formatPrice } = useCurrency();
  const { user } = useAuth();
  const hasPurchased = !!(user && user.has_purchased);
  // Cache-hydration helper: read JSON-serialised cache; default = empty array
  const hydrate = (key) => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const persist = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* localStorage blocked */ }
  };

  const [featured, setFeatured] = useState(() => hydrate('jsf_featured_cache') || []);
  const [signatures, setSignatures] = useState(() => hydrate('jsf_signatures_cache') || []);
  const [collections, setCollections] = useState(() => hydrate('jsf_collections_cache') || []);
  const [testimonials, setTestimonials] = useState(() => hydrate('jsf_testimonials_cache') || []);
  const [team, setTeam] = useState(() => hydrate('jsf_team_cache') || []);
  // Hydrate settings — first try `window.__JSF_SETTINGS__` (pre-fetched in index.html before React mounted),
  // then fall back to localStorage cache. Either way, the user's hero is ready before first paint.
  const [settings, setSettings] = useState(() => {
    if (typeof window !== 'undefined' && window.__JSF_SETTINGS__) return window.__JSF_SETTINGS__;
    return hydrate('jsf_settings_cache') || {};
  });
  const [settingsLoaded, setSettingsLoaded] = useState(() => {
    if (typeof window !== 'undefined' && window.__JSF_SETTINGS__) return true;
    try { return !!localStorage.getItem('jsf_settings_cache'); } catch { return false; }
  });
  const scrollRef = useRef(null);
  const isPausedRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/products`).then(res => {
      const sigs = res.data.filter(p => p.is_signature);
      const featuredProducts = res.data.filter(p => p.is_featured);
      const feat = featuredProducts.length > 0 ? featuredProducts : res.data.filter(p => !p.is_signature).slice(0, 8);
      setSignatures(sigs);
      setFeatured(feat);
      persist('jsf_signatures_cache', sigs);
      persist('jsf_featured_cache', feat);
    }).catch(console.error);
    axios.get(`${API}/collections`).then(res => {
      setCollections(res.data);
      persist('jsf_collections_cache', res.data);
    }).catch(console.error);
    axios.get(`${API}/feedback`).then(res => {
      setTestimonials(res.data);
      persist('jsf_testimonials_cache', res.data);
    }).catch(console.error);
    // Settings: reuse the bootstrap fetch from index.html if available (no duplicate network hit)
    const settingsPromise = (typeof window !== 'undefined' && window.__JSF_SETTINGS_PROMISE__)
      ? window.__JSF_SETTINGS_PROMISE__
      : axios.get(`${API}/settings`).then(r => r.data).catch(() => null);
    Promise.resolve(settingsPromise).then(data => {
      if (data) {
        setSettings(data);
        persist('jsf_settings_cache', data);
      }
      setSettingsLoaded(true);
    });
    axios.get(`${API}/team`).then(res => {
      setTeam(res.data);
      persist('jsf_team_cache', res.data);
    }).catch(console.error);
  }, []);

  // Shuffle testimonials if setting enabled
  const displayTestimonials = React.useMemo(() => {
    if (!settings.shuffle_testimonials && settings.shuffle_testimonials !== undefined) return testimonials;
    // Shuffle with a seed based on the day (so it doesn't change on every render)
    const shuffled = [...testimonials];
    const seed = new Date().getDate();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = (i * seed + seed) % (i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, [testimonials, settings.shuffle_testimonials]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || testimonials.length === 0) return;
    let animId;
    const scroll = () => {
      if (!isPausedRef.current) {
        el.scrollLeft += 0.5;
        if (el.scrollLeft >= el.scrollWidth - el.clientWidth) el.scrollLeft = 0;
      }
      animId = requestAnimationFrame(scroll);
    };
    animId = requestAnimationFrame(scroll);
    const pause = () => { isPausedRef.current = true; };
    const resume = () => { isPausedRef.current = false; };
    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);
    el.addEventListener('touchstart', pause, { passive: true });
    el.addEventListener('touchend', resume);
    return () => {
      cancelAnimationFrame(animId);
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
      el.removeEventListener('touchstart', pause);
      el.removeEventListener('touchend', resume);
    };
  }, [testimonials]);

  const trust = settings.trust_stats || {};

  // VIP status — show a discreet badge in the hero greeting for customers
  // who've crossed the configured threshold (default 3 paid orders).
  const [vip, setVip] = useState({ is_vip: false, order_count: 0 });
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    axios.get(`${API}/customer/vip-status`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setVip(r.data || { is_vip: false })).catch(() => {});
  }, [user]);

  return (
    <div data-testid="home-page">
      <SEOHead
        title={settings?.og_meta?.title ? null : null}
        description={settings?.og_meta?.description || "EDP bottled at 30% oil concentration — Extrait-level performance from JS Fragrance. Long-lasting luxury fragrances na affordable. Hand-blended in the Philippines. Shop signature scents and inspired-by classics."}
        image={settings?.og_meta?.image || undefined}
        url="https://jsfragrances.com/"
      />
      <OrganizationSchema />
      {/* 1. Hero */}
      <section className="relative h-[90vh] sm:h-screen flex items-center bg-[#0A0A0A]" data-testid="hero-section">
        <div className="absolute inset-0">
          {/* Show the resolved hero only once settings have loaded (or were hydrated from cache).
              This prevents a flash of the default hero before the user's custom upload renders. */}
          {settingsLoaded && (
            <img
              src={settings.hero_image || DEFAULT_HERO_IMG}
              alt="JS Fragrance"
              className="w-full h-full object-cover animate-[fadeIn_400ms_ease-out_forwards]"
              fetchPriority="high"
            />
          )}
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl animate-fade-in-up">
            <img src={LOGO_URL} alt="JS Fragrance" className="h-24 sm:h-32 w-auto mb-6" />
            {/* Personalized greeting for logged-in ambassadors / admins.
                Falls back to the generic brand eyebrow for everyone else.
                We pull the live ambassador rate from the wholesale config so
                a single tier-rate change auto-propagates here. */}
            {(() => {
              const isPartner = user && ['ambassador', 'admin', 'super_admin'].includes(user.role);
              if (isPartner) {
                const firstName = (user.name || '').trim().split(/\s+/)[0] || 'Partner';
                return (
                  <div data-testid="hero-personalized-greeting" className="mb-4">
                    <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Welcome back, {firstName}</p>
                    <p className="text-white/55 text-sm font-sans mt-1">
                      Your wholesale rate is locked at <span className="text-[#D4AF37] font-medium">₱255 / bottle</span> · Earn <span className="text-white">₱100 profit</span> per sale.
                    </p>
                  </div>
                );
              }
              // VIP customer — 3+ paid orders.
              if (vip.is_vip && user) {
                const firstName = (user.name || '').trim().split(/\s+/)[0] || 'there';
                return (
                  <div data-testid="hero-vip-greeting" className="mb-4">
                    <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] font-sans flex items-center gap-2">
                      <Crown className="w-4 h-4" /> VIP · Welcome back, {firstName}
                    </p>
                    <p className="text-white/55 text-sm font-sans mt-1">
                      {vip.order_count} orders and counting · thank you for keeping our story alive.
                    </p>
                  </div>
                );
              }
              return <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4 font-sans">Our Quality. Our Distinction.</p>;
            })()}
            <h1 className="hero-headline text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-light text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Luxury Should Be<br />
              <span className="text-[#D4AF37]">Experienced</span>
            </h1>
            <p className="text-white/60 text-base mb-8 max-w-lg font-light">
              EDP bottled at 30% oil concentration — Extrait-level fragrances crafted for presence, depth, and character. Without the inflated cost.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              {(() => {
                const isPartner = user && ['ambassador', 'admin', 'super_admin'].includes(user.role);
                return (
                  <Link
                    to={isPartner ? "/ambassador" : "/shop"}
                    className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black font-medium px-8 py-3.5 hover:bg-[#F3E5AB] transition-colors text-sm uppercase tracking-[0.18em]"
                    data-testid="hero-shop-button"
                  >
                    <ShoppingBag className="w-4 h-4" /> {isPartner ? 'My Dashboard' : 'Shop Now'}
                  </Link>
                );
              })()}
              <Link
                to="/scent-finder"
                className="inline-flex items-center justify-center gap-3 border border-white/20 text-white font-medium px-8 py-3.5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-[0.15em]"
                data-testid="hero-finder-button"
              >
                <Compass className="w-4 h-4" /> Find Your Scent
              </Link>
            </div>
            <p className="text-white/40 text-xs mt-4 font-sans" data-testid="hero-helper-text">
              ✓ Cash on Delivery available · ✓ Nationwide shipping · ✓ Same-day Metro Manila
            </p>
          </div>
        </div>
      </section>

      {/* How-to-Order — 3-step guide right below hero so first-time buyers
          immediately know the process. Buyers who don't see a clear path drop
          off here, and they DM asking "how to order?" which is the exact
          friction we're killing with this section. */}
      <section className="bg-[#0A0A0A] border-b border-white/5 py-10 sm:py-14" data-testid="how-to-order">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">Easy Process</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              How to Order in 3 Steps
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              { n: 1, title: 'Choose Your Scent', body: 'Browse our shop or take the 60-second Scent Quiz. Pwedeng filter by gender, family, or budget.', cta: { to: '/shop', label: 'Browse Shop' }, icon: ShoppingBag },
              { n: 2, title: 'Add to Cart & Checkout', body: 'Tap "Add to Cart" sa product page. Then fill in shipping details — pwede COD or pay online.', cta: { to: '/cart', label: 'View Cart' }, icon: Package },
              { n: 3, title: 'We Ship Within 24h', body: 'Metro Manila gets it same-day or next-day. Provinces 2–4 days nationwide. Track via SMS.', cta: { to: '/shipping-policy', label: 'Shipping Info' }, icon: Compass },
            ].map(step => (
              <div key={step.n} className="bg-[#141414] border border-white/5 p-5 sm:p-6 hover:border-[#D4AF37]/30 transition-colors group" data-testid={`step-${step.n}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                    <span className="text-[#D4AF37] text-sm font-medium font-sans">{step.n}</span>
                  </div>
                  <step.icon className="w-4 h-4 text-[#D4AF37]/50 group-hover:text-[#D4AF37] transition-colors" />
                </div>
                <h3 className="text-white text-lg mb-2 font-sans font-medium">{step.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed font-sans mb-3">{step.body}</p>
                <Link to={step.cta.to} className="inline-flex items-center gap-1.5 text-[#D4AF37] text-xs uppercase tracking-[0.15em] font-medium hover:text-white transition-colors" data-testid={`step-${step.n}-cta`}>
                  {step.cta.label} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2.5 bg-[#D4AF37] text-black px-8 py-3 text-sm uppercase tracking-[0.2em] font-medium hover:bg-[#F3E5AB] transition-colors"
              data-testid="how-to-order-shop-cta"
            >
              <ShoppingBag className="w-4 h-4" /> Start Shopping
            </Link>
          </div>
        </div>
      </section>

      {/* 1.5 — Trust strip (#16) */}
      <TrustStrip />

      {/* 1.6 — Scent Quiz CTA (#18) */}
      <section className="bg-[#0A0A0A] py-10 sm:py-14 border-b border-white/5" data-testid="quiz-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-1.5 font-sans flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Take The Scent Quiz
            </p>
            <h3 className="text-2xl sm:text-3xl tracking-tight font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Find your signature in 60 seconds.
            </h3>
          </div>
          <Link
            to="/scent-quiz"
            data-testid="home-quiz-cta-btn"
            className="btn-shimmer flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-medium font-sans hover:bg-[#E5C75A] transition-colors"
          >
            Start the quiz <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* 2. Collections / Categories */}
      {collections.length > 0 && (
        <section className="py-16 sm:py-24" data-testid="categories-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealOnScroll>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-center text-white mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Collections
              </h2>
            </RevealOnScroll>
            <div className={`grid grid-cols-1 ${collections.length === 1 ? 'max-w-lg mx-auto' : collections.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4 sm:gap-6`}>
              {collections.map((col, idx) => {
                const isLocked = col.members_only && !hasPurchased;
                const inner = (
                  <div className="group relative aspect-[4/3] overflow-hidden bg-[#141414] block" data-testid={`collection-${col.id}`}>
                    <LazyImage src={col.image_url} alt={col.title} className={`w-full h-full transition-all duration-700 ${isLocked ? 'opacity-30 group-hover:opacity-40 blur-sm' : 'opacity-70 group-hover:opacity-90 group-hover:scale-105'}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-2 mb-1">
                        {col.members_only && (
                          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 bg-[#D4AF37] text-black font-sans font-bold rounded-sm">
                            <Lock className="w-2.5 h-2.5" /> Members
                          </span>
                        )}
                        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-sans">{col.subtitle}</p>
                      </div>
                      <h3 className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{col.title}</h3>
                      {isLocked && (
                        <p className="text-white/70 text-xs sm:text-sm font-sans mt-2 max-w-sm leading-relaxed">
                          {col.members_teaser || 'Unlock this collection after your first purchase. Members-only access.'}
                        </p>
                      )}
                    </div>
                    {isLocked && (
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 pointer-events-none">
                        <Lock className="w-7 h-7 text-[#D4AF37]" />
                      </div>
                    )}
                  </div>
                );
                return (
                  <RevealOnScroll key={col.id} delay={idx * 100}>
                    {isLocked ? (
                      <Link to="/login" data-testid={`collection-locked-${col.id}`} className="block">
                        {inner}
                      </Link>
                    ) : (
                      <Link to={col.link_url} className="block">
                        {inner}
                      </Link>
                    )}
                  </RevealOnScroll>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 3. Best Sellers / Featured */}
      <section className="py-16 sm:py-24 bg-[#0A0A0A]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2 font-sans">Curated</p>
                <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Best Sellers
                </h2>
              </div>
              <Link to="/shop" className="text-sm uppercase tracking-[0.15em] text-white/50 hover:text-[#D4AF37] transition-colors flex items-center gap-2 font-sans">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </RevealOnScroll>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
            {featured.map((product, idx) => (
              <RevealOnScroll key={product.id} delay={idx * 60}>
                <ProductCard product={product} />
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Scent Finder CTA */}
      <RevealOnScroll>
        <section className="py-20 sm:py-28 relative overflow-hidden" data-testid="finder-cta-section">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent" />
          <div className="max-w-3xl mx-auto px-4 text-center relative z-10">
            <Compass className="w-10 h-10 text-[#D4AF37] mx-auto mb-5" />
            <h2 className="text-3xl sm:text-5xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Not sure what to pick?
            </h2>
            <p className="text-white/50 text-base mb-8 max-w-lg mx-auto font-light">
              Take our 30-second quiz. We&apos;ll match you with your perfect scent based on your vibe, preference, and desired strength.
            </p>
            <Link
              to="/scent-finder"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-medium px-10 py-4 hover:bg-[#F3E5AB] transition-colors text-sm uppercase tracking-[0.15em]"
              data-testid="finder-cta-button"
            >
              <Compass className="w-4 h-4" /> Take the Quiz
            </Link>
          </div>
        </section>
      </RevealOnScroll>

      {/* 5. Why Choose JS Fragrance — Trust Builders */}
      <section className="py-16 sm:py-24 bg-[#0A0A0A]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center mb-14">
              <p className="text-xs uppercase tracking-[0.3em] text-[#1C7D3F] mb-3 font-sans">Why Choose Us</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                The JS Fragrance Difference
              </h2>
            </div>
          </RevealOnScroll>

          {/* Stats Counter */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 mb-14">
            {[
              { value: trust.bottles_sold || '5,000+', label: 'Bottles Sold', icon: Package },
              { value: trust.happy_customers || '2,000+', label: 'Happy Customers', icon: Star },
              { value: trust.concentration || 'EDP · 30% Oil · Extrait', label: 'Concentration', icon: Droplets },
              { value: trust.longevity_claim || '6-10 Hours', label: 'Long-Lasting', icon: Clock },
            ].map((stat, idx) => (
              <RevealOnScroll key={stat.label} delay={idx * 100}>
                <div className="text-center p-5 sm:p-6 border border-white/5 hover:border-[#1C7D3F]/40 transition-colors" data-testid={`trust-stat-${idx}`}>
                  <stat.icon className="w-5 h-5 text-[#1C7D3F] mx-auto mb-3" />
                  <p className="text-xl sm:text-2xl text-white font-light mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{stat.value}</p>
                  <p className="text-white/40 text-xs uppercase tracking-[0.15em] font-sans">{stat.label}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>

          {/* Guarantee Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: Shield, title: 'Quality Guaranteed', desc: 'EDP bottled at 30% oil concentration — Extrait-level strength. Richer, deeper, and longer-lasting than typical 15-20% EDPs.' },
              { icon: Droplets, title: 'Affordable Luxury', desc: 'Designer-inspired scents without the designer markup. Premium fragrances for everyone.' },
              { icon: Package, title: 'Fast PH Delivery', desc: 'Metro Manila: 3-5 days. Luzon/Visayas/Mindanao: 5-7 days. Secure packaging always.' },
            ].map((item, idx) => (
              <RevealOnScroll key={item.title} delay={idx * 100}>
                <div className="p-6 sm:p-8 border border-white/5 hover:border-[#1C7D3F]/30 transition-all duration-300 group" data-testid={`guarantee-${idx}`}>
                  <item.icon className="w-6 h-6 text-[#1C7D3F] mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="text-lg text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{item.title}</h4>
                  <p className="text-white/40 text-sm font-sans leading-relaxed">{item.desc}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Signature Creations */}
      {signatures.length > 0 && (
        <section className="py-16 sm:py-24 border-y border-[#D4AF37]/20" data-testid="signature-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealOnScroll>
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Crown className="w-5 h-5 text-[#D4AF37]" />
                  <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-sans font-medium">JS Originals</p>
                  <Crown className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Signature Creations
                </h2>
                <p className="text-white/40 text-sm mt-3 font-light max-w-lg mx-auto">
                  Our own crafted compositions — unique to JS Fragrance. Not inspired by anyone.
                </p>
              </div>
            </RevealOnScroll>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              {signatures.map((product, idx) => (
                <RevealOnScroll key={product.id} delay={idx * 120}>
                  <Link to={`/product/${product.id}`} className="group block" data-testid={`signature-card-${product.id}`}>
                    <div className="relative bg-[#141414] border border-[#D4AF37]/20 group-hover:border-[#D4AF37]/50 transition-all duration-300 overflow-hidden">
                      <div className="aspect-[3/4] overflow-hidden">
                        <LazyImage
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                        />
                      </div>
                      <div className="absolute top-0 left-0 bg-[#D4AF37] text-black px-3 py-1.5 flex items-center gap-1.5 z-10">
                        <Crown className="w-3.5 h-3.5" />
                        <span className="text-[10px] uppercase tracking-[0.15em] font-sans font-bold">JS Original</span>
                      </div>
                      <div className="p-4 sm:p-5">
                        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-1 font-sans">{product.gender}</p>
                        <h3 className="text-xl text-white group-hover:text-[#D4AF37] transition-colors mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          {product.name}
                        </h3>
                        <p className="text-white/40 text-sm font-light leading-relaxed mb-3 line-clamp-2">{product.description}</p>
                        {product.signature_tagline && (
                          <p className="text-white/50 text-xs font-sans italic border-t border-white/10 pt-2">{product.signature_tagline}</p>
                        )}
                        <p className="text-[#D4AF37] text-base font-sans font-medium mt-3">{formatPrice(product.base_price)}</p>
                      </div>
                    </div>
                  </Link>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7. Team Section */}
      {team.length > 0 && (
        <LazyMount minHeight={500}>
        <section className="py-16 sm:py-24 bg-[#0A0A0A]" data-testid="team-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealOnScroll>
              <div className="text-center mb-12">
                <p className="text-xs uppercase tracking-[0.3em] text-[#1C7D3F] mb-3 font-sans">The People Behind the Scent</p>
                <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Our Team
                </h2>
              </div>
            </RevealOnScroll>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {team.map((member, idx) => (
                <RevealOnScroll key={member.id} delay={idx * 80}>
                  <div className="group text-center" data-testid={`team-member-${member.id}`}>
                    <div className="relative w-28 h-28 sm:w-36 sm:h-36 mx-auto mb-4 rounded-full overflow-hidden border-2 border-[#1C7D3F]/30 group-hover:border-[#1C7D3F] transition-colors">
                      {member.image_url ? (
                        <LazyImage src={member.image_url} alt={member.name} className="w-full h-full" />
                      ) : (
                        <div className="w-full h-full bg-[#141414] flex items-center justify-center">
                          <Users className="w-8 h-8 text-white/20" />
                        </div>
                      )}
                    </div>
                    <h4 className="text-white text-base font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{member.name}</h4>
                    <p className="text-[#1C7D3F] text-xs uppercase tracking-[0.15em] font-sans mt-1">{member.role}</p>
                    {member.bio && <ExpandableBio bio={member.bio} testId={`team-bio-${member.id}`} />}
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          </div>
        </section>
        </LazyMount>
      )}

      {/* 8. Testimonials */}
      {testimonials.length > 0 && (
        <LazyMount minHeight={500}>
        <section className="py-16 sm:py-24" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
            <RevealOnScroll>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    What Our Clients Say
                  </h2>
                  <p className="text-white/40 text-sm font-sans mt-2">Real feedback from real customers</p>
                </div>
                <Link to="/reviews" className="text-[#D4AF37] text-xs uppercase tracking-[0.15em] hover:text-[#F3E5AB] transition-colors font-sans flex items-center gap-1" data-testid="view-all-reviews-link">
                  View All <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </RevealOnScroll>
          </div>
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto px-4 sm:px-6 lg:px-8 scrollbar-hide touch-pan-x"
            style={{ scrollBehavior: 'auto', WebkitOverflowScrolling: 'touch' }}
            data-testid="testimonials-scroller"
          >
            {[...displayTestimonials, ...displayTestimonials].map((fb, idx) => (
              <div key={`${fb.id}-${idx}`} className="flex-shrink-0 w-[300px] sm:w-[340px] bg-[#141414] border border-white/5 overflow-hidden group hover:border-white/10 transition-colors">
                {fb.image_url && (
                  <div className="h-[240px] overflow-hidden">
                    <LazyImage src={fb.image_url} alt={fb.customer_name} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i <= fb.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/20'}`} />
                    ))}
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed font-sans italic line-clamp-3">&ldquo;{fb.comment}&rdquo;</p>
                  <p className="text-[#D4AF37] text-xs mt-3 uppercase tracking-[0.15em] font-sans font-medium">{fb.customer_name}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
        </LazyMount>
      )}

      {/* Ambassador CTA — premium gold-accent banner */}
      <section className="relative border-y border-[#D4AF37]/20 overflow-hidden" data-testid="ambassador-cta-banner">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1C7D3F]/15 via-transparent to-[#D4AF37]/15 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4 font-sans">Ambassador Program</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-light leading-[1.1] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Sell our story.<br />
            <span className="text-[#D4AF37]">Build your income.</span>
          </h2>
          <p className="text-white/60 text-base sm:text-lg font-light max-w-2xl mx-auto leading-relaxed mb-8">
            Up to <strong className="text-white">40% commission</strong>. Hand-blended Filipino fragrance. A growing tribe of ambassadors carrying our name forward.
          </p>
          <Link
            to="/ambassador-apply"
            data-testid="home-ambassador-cta"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-8 py-4 text-xs uppercase tracking-[0.22em] font-sans font-bold hover:bg-[#F3E5AB] transition-colors"
          >
            Become an Ambassador <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-white/30 text-xs font-sans mt-4">No fees. No annual minimums. Real product. Real margins.</p>
        </div>
      </section>
    </div>
  );
}

function ExpandableBio({ bio, testId }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = bio && bio.length > 110;
  return (
    <div className="mt-2" data-testid={testId}>
      <p className={`text-white/70 text-xs sm:text-sm font-sans leading-relaxed ${expanded || !isLong ? '' : 'line-clamp-3'}`}>
        {bio}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="text-[#D4AF37] hover:text-[#E5C75A] text-[11px] uppercase tracking-[0.15em] font-sans font-medium mt-2 transition-colors"
          data-testid={`${testId}-toggle`}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}
